import { useParams } from "react-router-dom";
import { usePublicDiagnosticReport } from "../hooks/usePublicDiagnosticReport";
import AvatarIndicator from "../components/AvatarIndicator";
import Spinner from "../components/Spinner";
import { CategoryScore } from "../types/diagnostics";
import { Search, AlertCircle, Lightbulb, Megaphone, Shield, Sparkles, Gauge, Home } from "lucide-react";

export default function PublicDiagnosticReportPage() {
  const { token } = useParams<{ token: string }>();
  const { data: report, isLoading, isError, error } = usePublicDiagnosticReport(token);

  // Prepare data for the circular chart dynamically from category scores
  const scoreData = report
    ? report.categoryScores.map((cat: CategoryScore, i: number) => ({
        name: cat.category,
        value: cat.percentage,
        color: ["#4f46e5", "#db2777", "#10b981", "#ea580c"][i % 4],
        icon: [Sparkles, Megaphone, Gauge, Shield][i % 4],
      }))
    : [];

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10">
      {/* Header Section */}
      <header className="mt-5 mb-8 flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <Search className="text-indigo-600" size={24} />
            Website Diagnostic Report
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Generated by Strivre Services
          </p>
        </div>
        <a
          href="/"
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
        >
          <Home size={18} />
          Home
        </a>
      </header>

      {/* Loading State */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl text-center animate-fade-in">
            <div className="mb-4 flex justify-center">
              <div className="relative">
                <Spinner size="h-12 w-12" />
                <div className="absolute inset-0 animate-pulse"></div>
              </div>
            </div>
            <p className="text-slate-900 font-semibold mb-1">Loading Report</p>
            <p className="text-slate-600 text-sm">Please wait while we fetch your report...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="mb-6">
            <AlertCircle size={64} className="mx-auto text-red-500" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Report Not Found</h2>
          <p className="text-slate-600 text-lg mb-8">
            {error?.message === "404" || error?.message?.includes("404")
              ? "The diagnostic report you're looking for doesn't exist or has expired."
              : "We encountered an error while loading the report. Please try again later."}
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Home size={18} />
            Return to Home
          </a>
        </div>
      )}

      {/* Success State - Report Display */}
      {report && !isLoading && !isError && (
        <div className="space-y-8 animate-fade-in">
          {/* Website URL */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <p className="text-sm text-slate-600 mb-2">Analyzed Website</p>
            <a
              href={report.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg font-semibold text-indigo-600 hover:underline break-all"
            >
              {report.url}
            </a>
          </div>

          {/* 1. Top Section: Hero Bento Grid & Activity Rings */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Visualizer Card */}
            <div className="lg:col-span-5 bg-white rounded-3xl border border-slate-200 shadow-sm p-8 flex flex-row items-center justify-center gap-8 relative overflow-hidden group min-h-[300px]">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

              {/* Left Side: Avatar */}
              <div className="flex-shrink-0">
                <AvatarIndicator state={report.visualIndicator} />
              </div>

              {/* Right Side: Score Result */}
              <div className="flex flex-col items-center md:items-start">
                <div className="bg-slate-50/50 backdrop-blur-sm px-8 py-6 rounded-[2.5rem] border border-slate-100 shadow-inner transition-all group-hover:bg-white group-hover:shadow-md">
                  <span className="text-7xl font-black text-slate-900 tracking-tighter leading-none block">
                    {report.overallPercentage}
                    <span className="text-3xl text-indigo-600 font-bold ml-1">%</span>
                  </span>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3 block">
                    Overall Health
                  </span>
                </div>
              </div>
            </div>

            {/* Metric Tiles */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {scoreData.map((s: any) => (
                <div key={s.name} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-col justify-between hover:border-blue-200 transition-all group">
                  <div className="flex justify-between items-start mb-4">
                    <div
                      className="p-3 rounded-2xl bg-slate-50 group-hover:bg-white transition-colors border border-transparent group-hover:border-slate-100"
                      style={{ color: s.color }}
                    >
                      <s.icon size={24} />
                    </div>
                    <span className="text-4xl font-black tracking-tighter" style={{ color: s.color }}>
                      {s.value}%
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight mb-2">
                      {s.name}
                    </h4>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${s.value}%`, backgroundColor: s.color }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Key Improvement Opportunities */}
          {report.recommendations.length > 0 && (
            <div className="bg-indigo-50/50 rounded-3xl border border-indigo-100 p-8 shadow-sm">
              <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="p-2 bg-indigo-600 rounded-lg text-white">
                  <Lightbulb size={20} />
                </div>
                Key Improvement Opportunities
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.recommendations.map((rec: string, i: number) => (
                  <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-indigo-50 shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </span>
                    <p
                      className="text-slate-700 text-sm font-normal leading-relaxed text-left"
                      dangerouslySetInnerHTML={{ __html: rec }}
                    ></p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. Mid CTA Section */}
          <div className="relative bg-gradient-to-br from-blue-600 to-indigo-800 rounded-[2.5rem] p-12 text-white text-center shadow-2xl overflow-hidden group">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold mb-4 tracking-tight">Ready to Take Your Website Further?</h3>
              <p className="text-blue-100 text-lg leading-relaxed max-w-2xl mx-auto mb-8 font-normal">
                Whether you want a faster website, a stronger branding, or a smoother customer experience, 
                we're here to help turn insights into real improvements.
              </p>
              <button className="px-10 py-4 bg-white text-blue-700 font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all active:scale-95">
                Contact Us
              </button>
            </div>
          </div>

          {/* 4. Report Summary Section */}
          {report.summaryReport.length > 0 && (
            <div className="bg-white rounded-3xl border border-slate-200 p-10 shadow-sm relative">
              <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                <Megaphone size={22} className="text-indigo-600" />
                Report Summary
              </h3>
              <div className="space-y-6 text-left border-l-4 border-indigo-100 pl-8 ml-2">
                {report.summaryReport.map((para: string, i: number) => (
                  <p
                    key={i}
                    className="text-slate-600 leading-relaxed text-md font-normal"
                    dangerouslySetInnerHTML={{ __html: para }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 5. Unlock Full Report CTA */}
          <div className="bg-slate-950 rounded-[2.5rem] border-none shadow-2xl p-12 text-white text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-blue-600/20 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-indigo-600/10 rounded-full blur-[100px]"></div>
            <div className="relative z-10">
              {/* <div className="inline-block px-4 py-1.5 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-black uppercase tracking-widest mb-6">
                Premium Report
              </div> */}
              <h3 className="text-3xl font-bold mb-4">Unlock the Full Website Health Report</h3>
              <p className="text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10 text-lg">
                Get the complete breakdown with detailed findings, improvement recommendations, and actionable insights.
              </p>
              <button className="px-12 py-5 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-500 shadow-2xl shadow-blue-600/20 transition-all transform hover:-translate-y-1">
                Buy Now
              </button>
            </div>
          </div>

          {/* 4. Report Metadata */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 text-center">
            {/* <p className="text-sm text-slate-600 mb-2">
              Report ID: <span className="font-mono font-semibold text-slate-900">{report.id}</span>
            </p> */}
            <p className="text-sm text-slate-600 mb-2">
              Generated: <span className="font-semibold text-slate-900">
                {new Date(report.createdDate).toLocaleString()}
              </span>
            </p>
            <p className="text-xs text-slate-500 mt-4">
              This report was generated by <span className="font-semibold">Strivre Services</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
